import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { fmtDate } from '../utils/format'
import {
  ArrowLeftIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  MapPinIcon
} from '@heroicons/react/24/outline'

const API_BASE_URL = '/api/v1/dev'

interface Allergen {
  id: number
  name: string
}

interface Ingredient {
  id: number
  name: string
  default_unit_name: string
  allergens: Allergen[]
}

interface Supplier {
  id: number
  trade_name: string
  nif: string
  phone: string
}

interface Receipt {
  id: number
  code: string
  received_at: string
  invoice_number: string
}

interface ReceiptItem {
  id: number
  brand: string
  purchase_date: string
  receipt: Receipt
}

interface InventoryBatch {
  id: number
  ingredient_id: number
  ingredient: Ingredient
  internal_code: string
  lot_number: string
  supplier_id: number
  supplier: Supplier
  quantity_total_g: number
  quantity_available_g: number
  best_before: string | null
  expiry_date: string | null
  opened_at: string | null
  closed_at: string | null
  status: 'unopened' | 'open' | 'depleted' | 'expired'
  allergens_snapshot: Allergen[]
  location: string | null
  usage_percentage: number
  days_until_expiry: number | null
  created_at: string
  receipt_item: ReceiptItem
}

interface BatchDetailResponse {
  data: {
    batch: InventoryBatch
    usage_percentage: number
    days_until_expiry: number | null
    is_available: boolean
  }
}

export default function InventoryBatchDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [batch, setBatch] = useState<InventoryBatch | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [newLocation, setNewLocation] = useState('')

  useEffect(() => {
    fetchBatchDetail()
  }, [id])

  const fetchBatchDetail = async () => {
    try {
      setLoading(true)
      setError('')

      const response = await fetch(`${API_BASE_URL}/inventory/batches/${id}`)
      const data: BatchDetailResponse = await response.json()

      if (response.ok) {
        setBatch(data.data.batch)
        setNewLocation(data.data.batch.location || '')
      } else {
        setError('Error al cargar el lote')
      }
    } catch (error) {
      setError('Error de conexión')
      console.error('Error fetching batch detail:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenBatch = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/inventory/batches/${id}/open`, {
        method: 'POST'
      })

      if (response.ok) {
        fetchBatchDetail()
      } else {
        const data = await response.json()
        setError(data.message || 'Error al abrir el lote')
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    } catch (error) {
      setError('Error de conexión')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleCloseBatch = async () => {
    if (!confirm('¿Está seguro de que desea cerrar este lote?')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/inventory/batches/${id}/close`, {
        method: 'POST'
      })

      if (response.ok) {
        fetchBatchDetail()
      } else {
        const data = await response.json()
        setError(data.message || 'Error al cerrar el lote')
      }
    } catch (error) {
      setError('Error de conexión')
    }
  }

  const handleUpdateLocation = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/inventory/batches/${id}/location`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ location: newLocation })
      })

      if (response.ok) {
        setShowLocationModal(false)
        fetchBatchDetail()
      } else {
        const data = await response.json()
        setError(data.message || 'Error al actualizar ubicación')
      }
    } catch (error) {
      setError('Error de conexión')
    }
  }

  const formatWeight = (grams: number) => {
    if (grams >= 1000) {
      return `${(grams / 1000).toFixed(2)} kg`
    }
    return `${Math.round(grams)} g`
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'unopened':
        return 'bg-blue-100 text-blue-800'
      case 'open':
        return 'bg-green-100 text-green-800'
      case 'depleted':
        return 'bg-gray-100 text-gray-800'
      case 'expired':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'unopened':
        return 'Sin abrir'
      case 'open':
        return 'Abierto'
      case 'depleted':
        return 'Agotado'
      case 'expired':
        return 'Expirado'
      default:
        return status
    }
  }

  const getExpiryWarning = (days: number | null) => {
    if (days === null) return null
    if (days < 0) return { color: 'text-red-600', icon: ExclamationTriangleIcon, text: 'Expirado', bgColor: 'bg-red-50' }
    if (days <= 3) return { color: 'text-red-600', icon: ExclamationTriangleIcon, text: `Quedan ${days} días`, bgColor: 'bg-red-50' }
    if (days <= 7) return { color: 'text-orange-600', icon: ClockIcon, text: `Quedan ${days} días`, bgColor: 'bg-orange-50' }
    if (days <= 14) return { color: 'text-yellow-600', icon: ClockIcon, text: `Quedan ${days} días`, bgColor: 'bg-yellow-50' }
    return { color: 'text-green-600', icon: CheckCircleIcon, text: `Quedan ${days} días`, bgColor: 'bg-green-50' }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!batch) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Lote no encontrado
        </div>
      </div>
    )
  }

  const expiryWarning = getExpiryWarning(batch.days_until_expiry)
  const WarningIcon = expiryWarning?.icon

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/inventory')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Volver al inventario
        </button>

        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-bold text-gray-900">
              Lote {batch.internal_code}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Lote de proveedor: <span className="font-mono">{batch.lot_number}</span>
            </p>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
            {batch.status === 'unopened' && (
              <button
                onClick={handleOpenBatch}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
              >
                Abrir Lote
              </button>
            )}
            {batch.status === 'open' && (
              <button
                onClick={handleCloseBatch}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                Cerrar Lote
              </button>
            )}
            <button
              onClick={() => setShowLocationModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              <MapPinIcon className="h-5 w-5 mr-2" />
              Ubicación
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Status Badge */}
      <div className="mb-6">
        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(batch.status)}`}>
          {getStatusText(batch.status)}
        </span>
      </div>

      {/* Expiry Warning */}
      {expiryWarning && WarningIcon && (
        <div className={`mb-6 p-4 rounded-lg ${expiryWarning.bgColor}`}>
          <div className="flex items-center">
            <WarningIcon className={`h-6 w-6 ${expiryWarning.color} mr-3`} />
            <div>
              <h3 className={`text-sm font-medium ${expiryWarning.color}`}>
                Fecha de caducidad
              </h3>
              <p className={`text-sm ${expiryWarning.color}`}>
                {expiryWarning.text} - Caduca el {fmtDate(batch.best_before || batch.expiry_date!)}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ingredient Info */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Información del Ingrediente</h2>
            </div>
            <div className="px-6 py-4">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Ingrediente</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">
                    {batch.ingredient?.name || 'N/A'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Unidad por defecto</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {batch.ingredient?.default_unit_name || 'N/A'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Marca</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {batch.receipt_item?.brand || 'N/A'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Ubicación</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {batch.location || 'Sin asignar'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Quantity Info */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Cantidades</h2>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Cantidad total</span>
                    <span className="font-medium text-gray-900">{formatWeight(batch.quantity_total_g)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Disponible</span>
                    <span className="font-medium text-green-600">{formatWeight(batch.quantity_available_g)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Consumido</span>
                    <span className="font-medium text-gray-900">
                      {formatWeight(batch.quantity_total_g - batch.quantity_available_g)}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Porcentaje usado</span>
                    <span className="font-medium text-gray-900">{(batch.usage_percentage || 0).toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all"
                      style={{ width: `${batch.usage_percentage || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Allergens */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Alérgenos</h2>
            </div>
            <div className="px-6 py-4">
              {batch.allergens_snapshot && batch.allergens_snapshot.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {batch.allergens_snapshot.map((allergen) => (
                    <span
                      key={allergen.id}
                      className="inline-flex px-3 py-1 text-sm font-medium bg-yellow-100 text-yellow-800 rounded-full"
                    >
                      {allergen.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Sin alérgenos declarados</p>
              )}
            </div>
          </div>

          {/* Consumption History - Placeholder */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Historial de Consumos</h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-500 text-center py-8">
                Los consumos se registrarán cuando se utilice este lote en órdenes de producción
              </p>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Supplier Info */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Proveedor</h2>
            </div>
            <div className="px-6 py-4">
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Nombre</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {batch.supplier?.trade_name || 'N/A'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">NIF</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">
                    {batch.supplier?.nif || 'N/A'}
                  </dd>
                </div>
                {batch.supplier?.phone && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Teléfono</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {batch.supplier.phone}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Receipt Info */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Recepción</h2>
            </div>
            <div className="px-6 py-4">
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Código de recepción</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">
                    {batch.receipt_item?.receipt?.code || 'N/A'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Fecha de recepción</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {batch.receipt_item?.receipt?.received_at
                      ? fmtDate(batch.receipt_item.receipt.received_at)
                      : 'N/A'}
                  </dd>
                </div>
                {batch.receipt_item?.receipt?.invoice_number && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Nº Factura</dt>
                    <dd className="mt-1 text-sm text-gray-900 font-mono">
                      {batch.receipt_item.receipt.invoice_number}
                    </dd>
                  </div>
                )}
                {batch.receipt_item?.purchase_date && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Fecha de compra</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {fmtDate(batch.receipt_item.purchase_date)}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Dates */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Fechas</h2>
            </div>
            <div className="px-6 py-4">
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Fecha de caducidad</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(batch.best_before || batch.expiry_date)
                      ? fmtDate(batch.best_before || batch.expiry_date!)
                      : 'No especificada'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Fecha de creación</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {fmtDate(batch.created_at)}
                  </dd>
                </div>
                {batch.opened_at && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Fecha de apertura</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {fmtDate(batch.opened_at)}
                    </dd>
                  </div>
                )}
                {batch.closed_at && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Fecha de cierre</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {fmtDate(batch.closed_at)}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Actualizar Ubicación
                      </h3>
                      <button
                        onClick={() => setShowLocationModal(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ubicación física del lote
                      </label>
                      <input
                        type="text"
                        value={newLocation}
                        onChange={(e) => setNewLocation(e.target.value)}
                        placeholder="Ej: Cámara 1 - Estantería A - Nivel 2"
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                      <p className="mt-2 text-sm text-gray-500">
                        Especifica dónde se encuentra físicamente este lote en el almacén
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleUpdateLocation}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Actualizar
                </button>
                <button
                  type="button"
                  onClick={() => setShowLocationModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
